import _ from 'lodash';
import {
  array,
  mixed,
  number,
  object,
  SchemaOf,
  string,
  boolean,
  ValidationError,
  TestContext,
  AnySchema,
} from 'yup';
import { AnyObject } from 'yup/lib/types';

import { defaultPeriodValue } from '@/react/edge/edge-stacks/components/RetryDeployPeriod';
import { staggerConfigValidation } from '@/react/edge/edge-stacks/components/StaggerFieldset';
import { relativePathValidation } from '@/react/portainer/gitops/RelativePathFieldset/validation';

import { envVarValidation } from '@@/form-components/EnvironmentVariablesFieldset';
import { buildUniquenessTest } from '@@/form-components/validate-unique';

import { Artifact, DriftMode, FormValues, WorkflowEngine } from './types';
import { DeploymentType } from './detectDeploymentType';
import { deriveDeployOrder, DependencyGraphNode } from './deployOrder';

// A Go duration: one or more decimal numbers each followed by a unit.
const DURATION_PATTERN = /^([0-9]+(\.[0-9]+)?(ns|us|ms|s|m|h))+$/;
const DURATION_UNIT_PATTERN = /([0-9]+(?:\.[0-9]+)?)(ns|us|ms|s|m|h)/g;

const SECONDS_PER_UNIT: Record<string, number> = {
  ns: 1e-9,
  us: 1e-6,
  ms: 1e-3,
  s: 1,
  m: 60,
  h: 3600,
};

// Mirrors the server's minReconcileInterval: detecting drift costs one dry-run
// apply per resource, so a shorter interval is a denial of service against the
// environments' own API servers.
const MIN_RECONCILE_INTERVAL = '1m';
const MIN_RECONCILE_INTERVAL_SECONDS = 60;

/** Reads a Go duration as seconds. Assumes it already matches DURATION_PATTERN. */
export function durationSeconds(duration: string): number {
  let seconds = 0;

  for (const [, amount, unit] of duration.matchAll(DURATION_UNIT_PATTERN)) {
    seconds += Number(amount) * SECONDS_PER_UNIT[unit];
  }

  return seconds;
}

function isReconciled(ctx: { options?: { context?: unknown } }): boolean {
  const values = ctx.options?.context as FormValues | undefined;

  return values?.engine === 'reconcile';
}

// Mirrors the backend's edge.IsValidEdgeStackName, a docker-compose-compatible subset.
const NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{0,254}$/;

/** An RFC 1123 label, which is what Kubernetes accepts as a namespace. */
const KUBERNETES_NAME_PATTERN = /^$|^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/;
const NAME_MESSAGE =
  'must contain only lowercase letters, decimal digits, dashes, and underscores, and must begin with a lowercase letter or decimal digit.';

function nameSchema(label: string) {
  return string()
    .required(`${label} is required.`)
    .matches(NAME_PATTERN, `${label} ${NAME_MESSAGE}`);
}

function artifactSchema(): SchemaOf<Artifact> {
  return object({
    name: nameSchema('Name'),
    dependencies: array(string().required()).default([]),
    // The reconciliation engine applies to Kubernetes, so a Compose artifact is
    // refused here rather than accepted and failed on every environment it is
    // published to.
    deploymentType: mixed<DeploymentType>()
      .oneOf(Object.values(DeploymentType))
      .required()
      .when('$engine', {
        is: 'reconcile',
        then: (schema) =>
          schema.oneOf(
            [DeploymentType.Kubernetes],
            'The reconciliation engine deploys Kubernetes resources only.'
          ),
      }),
    // An Edge stack deploys one entry file. The reconciliation engine has none:
    // it applies whatever its paths resolve to, which may be several files, a
    // directory, or a chart.
    files: array(
      object({
        sourceId: number().required('File source is required'),
        path: string().required('File path is required'),
        ref: string().required('Git reference is required'),
      })
    ).min(1, 'File is required'),
    // Which list has to be filled depends on the engine, which lives on the root
    // form: Formik passes it as the validation context.
    // Any of the three targeting kinds satisfies a reconciled artifact, so the
    // requirement is checked across them rather than on one list.
    targets: array(number().required()).test(
      'targets-required',
      'At least one target is required.',
      function targetsRequired(value) {
        const artifact = this.parent as Artifact;

        return isReconciled(this)
          ? !!(
              artifact.environmentGroups?.length ||
              artifact.environments?.length ||
              value?.length
            )
          : !!value?.length;
      }
    ),
    environmentGroups: array(number().required()),
    environments: array(number().required()),
    helm: object({
      enabled: boolean().default(false),
      valuesFiles: array(string().required()),
      // Checked here as well as on the server, so an invalid name is a field
      // error rather than a Helm failure on every environment at once.
      namespace: string()
        .default('')
        .matches(
          KUBERNETES_NAME_PATTERN,
          'A namespace is lowercase letters, digits and dashes, starting and ending with a letter or digit'
        ),
    }),
    config: object({
      envVars: envVarValidation(),
      prePullImage: boolean().default(false),
      retryDeploy: boolean().default(false),
      retryPeriod: number().default(defaultPeriodValue),
      parallelConfig: staggerConfigValidation(),
      registryId: number(),
      relativePath: relativePathValidation(),
      useManifestNamespaces: boolean().default(false),
      kubernetesPrune: boolean().default(true),
      kubernetesForceApply: boolean().default(false),
      driftMode: mixed<DriftMode>()
        .oneOf(['disabled', 'warn', 'enabled'])
        .default('warn'),
    }),
  });
}

export function reviewStepValidationSchema(): SchemaOf<FormValues> {
  return object({
    name: nameSchema('Name'),
    engine: mixed<WorkflowEngine>()
      .oneOf(['edgeStack', 'reconcile'])
      .default('edgeStack'),
    sources: array(number().required()).min(
      1,
      'At least one source is required.'
    ),
    reconcile: reconcileSchema(),
    artifacts: withArtifactCrossChecks(artifactSchema()),
  });
}

/**
 * The workflow-level schedule, checked on the step that asks for it.
 *
 * The interval is entered beside the engine on the artifacts step, so that is
 * where it has to be caught: a rule the last step enforces lets a workflow
 * carry an unusable value through every screen before saying so.
 *
 * Required only of the reconciliation engine. An Edge stack workflow never
 * shows the field, and refusing to continue over one nobody can see is worse
 * than ignoring it.
 */
function reconcileSchema() {
  return object({
    // Validated as a Go duration because that is what the server parses. The
    // engine rejects anything under a minute, so saying so here saves a round
    // trip to find out. There is no value that means never.
    interval: string()
      .default('')
      .when('$engine', {
        is: 'reconcile',
        then: (schema) => schema.required('A reconcile interval is required.'),
      })
      .test(
        'duration',
        'Interval must be a duration such as 5m or 1h30m.',
        (value) => !value || DURATION_PATTERN.test(value)
      )
      .test(
        'min-interval',
        `Interval must be at least ${MIN_RECONCILE_INTERVAL}.`,
        (value) =>
          !value ||
          !DURATION_PATTERN.test(value) ||
          durationSeconds(value) >= MIN_RECONCILE_INTERVAL_SECONDS
      ),
  });
}

type ArtifactsStepsArtifactValidationSchema = SchemaOf<
  Pick<Artifact, 'name' | 'dependencies' | 'files'>
>;

export function artifactsStepValidationSchema() {
  return object({
    sources: array(number().required()).min(
      1,
      'At least one source is required.'
    ),
    // The engine and its schedule are chosen on this step, so they are checked
    // on it rather than left to the last one.
    reconcile: reconcileSchema(),
    artifacts: withArtifactCrossChecks(
      artifactSchema().pick([
        'name',
        'dependencies',
        'files',
      ]) as ArtifactsStepsArtifactValidationSchema
    ),
  });
}

/**
 * Validates only the one artifact being configured on this step; the others
 * either haven't been configured yet or already passed their own step.
 */
export function artifactConfigStepValidationSchema(index: number) {
  return object({
    sources: array(number().required()).min(
      1,
      'At least one source is required.'
    ),
    artifacts: array().test(
      'current-artifact-config-valid',
      "Complete this artifact's configuration before continuing.",
      function test(artifacts) {
        const artifact = artifacts?.[index];
        if (!artifact) {
          return true;
        }

        try {
          // The engine lives on the root form and reaches the artifact rules as
          // the validation context, which a nested validation has to pass on.
          artifactSchema().validateSync(artifact, {
            abortEarly: false,
            context: this.options.context,
          });
          return true;
        } catch (error) {
          if (!(error instanceof ValidationError)) {
            throw error;
          }

          const firstError = error.inner[0] ?? error;
          return this.createError({
            path: firstError.path
              ? `${this.path}[${index}].${firstError.path}`
              : `${this.path}[${index}]`,
            message: firstError.message,
          });
        }
      }
    ),
  });
}

/** Cross-artifact checks, each reported on the offending artifact's field. */
function withArtifactCrossChecks<T extends AnySchema>(artifactSchema: T) {
  return array(artifactSchema)
    .test(
      'unique-names',
      'Name must be unique',
      buildUniquenessTest(() => 'Name must be unique', 'name')
    )
    .test(
      'valid-artifact-dependencies',
      buildDependenciesTest(
        'An artifact cannot depend on itself, an unknown artifact, or list the same dependency twice.',
        hasInvalidDependencies
      )
    )
    .test(
      'no-circular-artifact-dependencies',
      buildDependenciesTest(
        'Artifact dependencies cannot form a cycle.',
        isInDependencyCycle
      )
    );
}

type ArtifactDependenciesValue = {
  name?: string | null;
  dependencies?: Array<string | null | undefined> | null;
};

/**
 * Builds a yup array test reporting `message` on the `dependencies` field of
 * every artifact for which `isInvalid` returns true.
 */
function buildDependenciesTest(
  message: string,
  isInvalid: (
    artifact: DependencyGraphNode,
    artifacts: Array<DependencyGraphNode>
  ) => boolean
) {
  return (
    values: Array<ArtifactDependenciesValue> | undefined,
    context: TestContext<AnyObject>
  ) => {
    if (!values) {
      return true;
    }

    const artifacts = values.map((value) => ({
      name: value.name ?? '',
      dependencies: _.compact(value.dependencies),
    }));

    const errors = artifacts.flatMap((artifact, i) =>
      isInvalid(artifact, artifacts)
        ? [
            new ValidationError(
              message,
              artifact.dependencies,
              `${context.path}[${i}].dependencies`
            ),
          ]
        : []
    );

    return errors.length === 0 || new ValidationError(errors);
  };
}

// An artifact cannot depend on itself, an unknown artifact, or list the same dependency twice.
function hasInvalidDependencies(
  artifact: DependencyGraphNode,
  artifacts: Array<DependencyGraphNode>
) {
  const dependencies = artifact.dependencies ?? [];
  const names = new Set(artifacts.map(({ name }) => name));

  return (
    new Set(dependencies).size !== dependencies.length ||
    dependencies.some(
      (dependency) => dependency === artifact.name || !names.has(dependency)
    )
  );
}

// ponytail: re-sorts the whole graph per artifact (O(n²)); precompute once if artifact counts grow large.
function isInDependencyCycle(
  artifact: DependencyGraphNode,
  artifacts: Array<DependencyGraphNode>
) {
  const result = deriveDeployOrder(artifacts);
  return result.hasCycle && result.cyclicNames.includes(artifact.name);
}
